import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ProtectedRoute, PublicOnlyRoute } from './auth/RouteGuards';
import StartupSplash from './components/StartupSplash';
import Loading from './components/Loading';
import AppVersionGate from './components/AppVersionGate';

// Public pages (loaded immediately)
import Home from './pages/Home';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailVerificationPage from './pages/EmailVerificationPage';
import SecurityQuestionsPage from './pages/SecurityQuestionsPage';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import PickAvatar from './pages/PickAvatar';
import GameHistory from './pages/GameHistory';
import Wallet from './pages/Wallet';
import DepositPage from './pages/DepositPage';
import WithdrawPage from './pages/WithdrawPage';
import Tournaments from './pages/Tournaments';
import MyTournaments from './pages/MyTournaments';
import TournamentGuide from './pages/TournamentGuide';
import TournamentsPage from './pages/TournamentsPage';
import TournamentLobby from './pages/TournamentLobby';
import TournamentPlay from './pages/TournamentPlay';
import LeaderboardPage from './pages/LeaderboardPage';
import PlayWhot from './pages/PlayWhot';
import FriendsPage from './pages/FriendsPage';
import RoomsPage from './pages/RoomsPage';
import RoomWaitingPage from './pages/RoomWaitingPage';
import ReferralPage from './pages/ReferralPage';
import DepositConfirmPage from './pages/DepositConfirmPage';
import NotificationsPage from './pages/NotificationsPage';
import MatchCountdownPage from './pages/MatchCountdownPage';
import WhotRoomInvitationListener from './components/WhotRoomInvitationListener';
import StakeLifecycleListener from './components/StakeLifecycleListener';
import SupportLauncher from './components/SupportLauncher';
import Settings from './pages/Settings';
import AchievementsPage from './pages/AchievementsPage';
import MaintenancePage from './pages/MaintenancePage';

// Admin pages — code-split so they're only loaded after server-side admin verification
// This prevents admin JS bundles from being shipped to non-admin users (Finding 3)
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminTournaments = lazy(() => import('./pages/AdminTournaments'));
const AdminFriendsRoomsPage = lazy(() => import('./pages/AdminFriendsRoomsPage'));
const AdminNotificationsPage = lazy(() => import('./pages/AdminNotificationsPage'));
const AdminTournamentDetails = lazy(() => import('./pages/AdminTournamentDetails'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminTransactionsPage = lazy(() => import('./pages/AdminTransactionsPage'));
const AdminFinanceOverviewPage = lazy(() => import('./pages/AdminFinanceOverviewPage'));
const AdminFinanceWalletsPage = lazy(() => import('./pages/AdminFinanceWalletsPage'));
const AdminSystemConfigPage = lazy(() => import('./pages/AdminSystemConfigPage'));
const AdminLeaderboardRewardsPage = lazy(() => import('./pages/AdminLeaderboardRewardsPage'));
const AdminEmailSettingsHubPage = lazy(() => import('./pages/AdminEmailSettingsHubPage'));
const AdminWhotStakeSettingsPage = lazy(() => import('./pages/AdminWhotStakeSettingsPage'));
const AdminPromoCodesPage = lazy(() => import('./pages/AdminPromoCodesPage'));
const AdminAppVersionPage = lazy(() => import('./pages/AdminAppVersionPage'));
const AdminDeployButtonV2Page = lazy(() => import('./pages/AdminDeployButtonV2Page'));
const AdminEmergencyControlsPage = lazy(() => import('./pages/AdminEmergencyControlsPage'));
const PaymentOverviewPage = lazy(() => import('./pages/AdminPaymentsPages').then(m => ({ default: m.PaymentOverviewPage })));
const PaymentProvidersPage = lazy(() => import('./pages/AdminPaymentsPages').then(m => ({ default: m.PaymentProvidersPage })));
const PaymentDepositsPage = lazy(() => import('./pages/AdminPaymentsPages').then(m => ({ default: m.PaymentDepositsPage })));
const PaymentWithdrawalsPage = lazy(() => import('./pages/AdminPaymentsPages').then(m => ({ default: m.PaymentWithdrawalsPage })));
const PaymentTransactionsPage = lazy(() => import('./pages/AdminPaymentsPages').then(m => ({ default: m.PaymentTransactionsPage })));
const CustomerSupportAdminPage = lazy(() => import('./pages/CustomerSupportAdminPage'));
const AdminBroadcastCenterPage = lazy(() => import('./pages/AdminBroadcastCenterPage'));
const AdminBroadcastAnalyticsPage = lazy(() => import('./pages/AdminBroadcastAnalyticsPage'));
const CustomerSupportPage = lazy(() => import('./pages/CustomerSupportPage'));
const AdminAchievementsPage = lazy(() => import('./pages/AdminAchievementsPage'));
const AdminMatchInvestigationPage = lazy(() => import('./pages/AdminMatchInvestigationPage'));
const AdminSecurityPage = lazy(() => import('./pages/AdminSecurityPage'));
const AdminAuditPage = lazy(() => import('./pages/AdminAuditPage'));
const AdminStaffPage = lazy(() => import('./pages/AdminStaffPage'));
const StaffInvitationAcceptPage = lazy(() => import('./pages/StaffInvitationAcceptPage'));
const AdminSystemHealthPage = lazy(() => import('./pages/AdminSystemHealthPage'));
const AdminSoftwareAuditPage = lazy(() => import('./pages/AdminSoftwareAuditPage'));
const AdminPlatformAuditPage = lazy(() => import('./pages/AdminPlatformAuditPage'));
const AdminWhotOperationsOverviewPage = lazy(() => import('./pages/AdminWhotOperationsOverviewPage'));
const AdminWhotOperationsPage = lazy(() => import('./pages/AdminWhotOperationsPage'));

function RootPage() {
  const { user, initialized } = useAuth();
  if (!initialized) return <Loading variant="page" label="" />;
  return user ? <Home /> : <LandingPage />;
}

function LegacyConfigRedirect() {
  const [params] = useSearchParams();
  const view = params.get('view') || 'platform';
  const targets = { finance:'/admin/money/finance-settings', whot:'/admin/whot/settings', referral:'/admin/growth/referral-settings', prime:'/admin/growth/prime-settings', achievements:'/admin/achievements?view=definitions', platform:'/admin/system/platform-settings', identity:'/admin/system/signup-withdrawals', email:'/admin/system/email?tab=notifications' };
  return <Navigate to={targets[view] || targets.platform} replace />;
}

// Loading fallback for code-split components
function AdminFallback() {
  return <Loading variant="page" label="" />;
}

function MaintenanceBoundary({ children }) {
  const location = useLocation();
  const [maintenance, setMaintenance] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const recoveryPath = /^\/(login|register|verify-email|security-questions|forgot-password|reset-password)(\/|$)/.test(location.pathname)
    || location.pathname === '/admin'
    || location.pathname.startsWith('/admin/');

  React.useEffect(() => {
    let mounted = true;
    setChecked(false);
    if (recoveryPath) {
      setMaintenance(false);
      setChecked(true);
      return () => { mounted = false; };
    }
    let timer;
    const refreshStatus = () => fetch('/api/maintenance/status?path=' + encodeURIComponent(location.pathname), { credentials: 'include', cache: 'no-store' })
      .then(response => response.ok ? response.json() : null)
      .then(payload => {
        if (!mounted) return;
        setMaintenance(payload?.showMaintenance === true);
        if (payload?.showMaintenance === true) window.__9JAWIN_MAINTENANCE_MESSAGE__ = payload.message || '';
        setChecked(true);
      })
      .catch(() => { if (mounted) setChecked(true); });
    refreshStatus();
    timer = window.setInterval(refreshStatus, 5000);
    return () => { mounted = false; window.clearInterval(timer); };
  }, [location.pathname, recoveryPath]);

  if (!checked) return <Loading variant="page" label="Checking platform status…" />;
  if (maintenance && !recoveryPath) return <MaintenancePage message={window.__9JAWIN_MAINTENANCE_MESSAGE__ || ''} />;
  return children;
}

function LegacyStakeRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search, hash: location.hash }} state={location.state} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider><AppVersionGate><WhotRoomInvitationListener /><StakeLifecycleListener /><SupportLauncher /><StartupSplash><div className="app">
        <MaintenanceBoundary><Routes>
          <Route path="/" element={<RootPage />} />
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/security-questions" element={<ProtectedRoute><SecurityQuestionsPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route path="/profile/avatar" element={<ProtectedRoute><PickAvatar /></ProtectedRoute>} />
          <Route path="/game-history/:recordType/:recordId" element={<ProtectedRoute><GameHistory /></ProtectedRoute>} />
          <Route path="/game-history" element={<ProtectedRoute><GameHistory /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/wallet/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
            <Route path="/wallet/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
            <Route path="/wallet/history" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/wallet/deposit/confirm" element={<ProtectedRoute><DepositConfirmPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><Suspense fallback={<Loading variant="page" label="" />}><CustomerSupportPage /></Suspense></ProtectedRoute>} />
          <Route path="/support/new" element={<ProtectedRoute><Suspense fallback={<Loading variant="page" label="" />}><CustomerSupportPage /></Suspense></ProtectedRoute>} />
          <Route path="/support/tickets" element={<ProtectedRoute><Suspense fallback={<Loading variant="page" label="" />}><CustomerSupportPage /></Suspense></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/tournaments/how-to-play" element={<TournamentGuide />} />
          <Route path="/tournaments/my" element={<ProtectedRoute><MyTournaments /></ProtectedRoute>} />
          <Route path="/tournaments-page" element={<TournamentsPage />} />
          <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentLobby /></ProtectedRoute>} />
          <Route path="/tournaments/:id/play" element={<ProtectedRoute><TournamentPlay /></ProtectedRoute>} />
          {/* Admin routes — code-split into separate chunks via React.lazy.
              These bundles are NEVER loaded until a user first navigates to /admin,
              and only after the server-side admin check passes (handled inside each page).
              This prevents admin JS bundles from shipping to non-admin users. */}
          <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<AdminFallback />}><AdminPage /></Suspense></ProtectedRoute>}>
            <Route index element={<></>} />
            <Route path="users" element={<Suspense fallback={<AdminFallback />}><AdminUsersPage /></Suspense>} />
            <Route path="finance" element={<Suspense fallback={<AdminFallback />}><AdminFinanceOverviewPage /></Suspense>} />
          <Route path="finance/wallets" element={<Suspense fallback={<AdminFallback />}><AdminFinanceWalletsPage /></Suspense>} />
          <Route path="transactions" element={<Suspense fallback={<AdminFallback />}><AdminTransactionsPage /></Suspense>} />
            <Route path="config" element={<LegacyConfigRedirect />} />
            <Route path="money/finance-settings" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="finance" /></Suspense>} />
            <Route path="whot/settings" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="whot" /></Suspense>} />
            <Route path="growth/referral-settings" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="referral" /></Suspense>} />
            <Route path="growth/prime-settings" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="prime" /></Suspense>} />
            <Route path="system/platform-settings" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="platform" /></Suspense>} />
            <Route path="system/signup-withdrawals" element={<Suspense fallback={<AdminFallback />}><AdminSystemConfigPage initialView="identity" /></Suspense>} />
            <Route path="system/leaderboard-rewards" element={<Suspense fallback={<AdminFallback />}><AdminLeaderboardRewardsPage /></Suspense>} />
            <Route path="system/email" element={<Suspense fallback={<AdminFallback />}><AdminEmailSettingsHubPage /></Suspense>} />
            <Route path="whot-stake-settings" element={<Suspense fallback={<AdminFallback />}><AdminWhotStakeSettingsPage /></Suspense>} />
            <Route path="promo-codes" element={<Suspense fallback={<AdminFallback />}><AdminPromoCodesPage /></Suspense>} />
            <Route path="app-version" element={<Suspense fallback={<AdminFallback />}><AdminAppVersionPage /></Suspense>} />
            <Route path="deploy" element={<Suspense fallback={<AdminFallback />}><AdminDeployButtonV2Page /></Suspense>} />
            <Route path="email-settings" element={<Navigate to="/admin/system/email?tab=notifications" replace />} />
            <Route path="emergency/:control" element={<Suspense fallback={<AdminFallback />}><AdminEmergencyControlsPage /></Suspense>} />
            <Route path="payments" element={<Suspense fallback={<AdminFallback />}><PaymentOverviewPage /></Suspense>} />
            <Route path="payments/providers" element={<Suspense fallback={<AdminFallback />}><PaymentProvidersPage /></Suspense>} />
            <Route path="payments/deposits" element={<Suspense fallback={<AdminFallback />}><PaymentDepositsPage /></Suspense>} />
            <Route path="payments/withdrawals" element={<Suspense fallback={<AdminFallback />}><PaymentWithdrawalsPage /></Suspense>} />
            <Route path="payments/transactions" element={<Suspense fallback={<AdminFallback />}><PaymentTransactionsPage /></Suspense>} />
            <Route path="tournaments" element={<Suspense fallback={<AdminFallback />}><AdminTournaments /></Suspense>} />
            <Route path="rooms" element={<Suspense fallback={<AdminFallback />}><AdminFriendsRoomsPage /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<AdminFallback />}><AdminNotificationsPage /></Suspense>} />
            <Route path="tournaments/:id" element={<Suspense fallback={<AdminFallback />}><AdminTournamentDetails /></Suspense>} />
            <Route path="tournaments/:id/edit" element={<Suspense fallback={<AdminFallback />}><AdminTournamentDetails /></Suspense>} />
            <Route path="achievements" element={<Suspense fallback={<AdminFallback />}><AdminAchievementsPage /></Suspense>} />
            <Route path="whot/overview" element={<Suspense fallback={<AdminFallback />}><AdminWhotOperationsOverviewPage /></Suspense>} />
            <Route path="whot/operations" element={<Suspense fallback={<AdminFallback />}><AdminWhotOperationsPage /></Suspense>} />
            <Route path="/admin/match-investigation" element={<Suspense fallback={<AdminFallback />}><AdminMatchInvestigationPage /></Suspense>} />
            <Route path="support" element={<Suspense fallback={<AdminFallback />}><CustomerSupportAdminPage /></Suspense>} />
            <Route path="security" element={<Suspense fallback={<AdminFallback />}><AdminSecurityPage /></Suspense>} />
            <Route path="audit" element={<Suspense fallback={<AdminFallback />}><AdminAuditPage /></Suspense>} />
            <Route path="staff" element={<Suspense fallback={<AdminFallback />}><AdminStaffPage /></Suspense>} />
            <Route path="system-health" element={<Suspense fallback={<AdminFallback />}><AdminSystemHealthPage /></Suspense>} />
            <Route path="software-audit" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="dashboard" /></Suspense>} />
            <Route path="software-audit/new-scan" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="new-scan" /></Suspense>} />
            <Route path="software-audit/history" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="history" /></Suspense>} />
            <Route path="software-audit/findings" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="findings" /></Suspense>} />
            <Route path="software-audit/evidence" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="evidence" /></Suspense>} />
            <Route path="software-audit/reports" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="reports" /></Suspense>} />
            <Route path="software-audit/toolchain" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="toolchain" /></Suspense>} />
            <Route path="software-audit/service-security" element={<Suspense fallback={<AdminFallback />}><AdminSoftwareAuditPage section="service-security" /></Suspense>} />
            <Route path="software-audit/platform" element={<Suspense fallback={<AdminFallback />}><AdminPlatformAuditPage /></Suspense>} />
            <Route path="software-audit/platform/:category" element={<Suspense fallback={<AdminFallback />}><AdminPlatformAuditPage /></Suspense>} />
            <Route path="support/email-settings" element={<Navigate to="/admin/system/email?tab=support" replace />} />
            <Route path="broadcasts/:id/analytics" element={<Suspense fallback={<AdminFallback />}><AdminBroadcastAnalyticsPage /></Suspense>} />
            <Route path="broadcasts" element={<Suspense fallback={<AdminFallback />}><AdminBroadcastCenterPage /></Suspense>} />
          </Route>

          <Route path="/staff-invitation" element={<Suspense fallback={<AdminFallback />}><StaffInvitationAcceptPage /></Suspense>} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/play/whot/:matchId" element={<ProtectedRoute><PlayWhot /></ProtectedRoute>} />
          <Route path="/stake" element={<ProtectedRoute><LegacyStakeRedirect /></ProtectedRoute>} />
          <Route path="/stakes" element={<ProtectedRoute><LegacyStakeRedirect /></ProtectedRoute>} />
          <Route path="/match/:matchId/countdown" element={<ProtectedRoute><MatchCountdownPage /></ProtectedRoute>} />
          <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
          <Route path="/rooms" element={<ProtectedRoute><RoomsPage /></ProtectedRoute>} />
          <Route path="/room-waiting/:roomId" element={<ProtectedRoute><RoomWaitingPage /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
        </Routes></MaintenanceBoundary>
      </div></StartupSplash></AppVersionGate></AuthProvider>
    </Router>
  );
}

export default App;

// Release pipeline test: comment-only change.
